import Cookies from 'js-cookie';
import { useCallback } from 'react';
import { gql } from 'graphql-request';
import useSWRMutation from 'swr/mutation';
import { basketRequest, OutOfStockError } from '@hooks/basket/_utils';
import { track } from '@repo/ui/src/services/analytics/utils';
import { useCustomerIdToken } from '@hooks/customer';
import useCurrentUrl from '@repo/ui/src/hooks/useCurrentUrl';
import { checkoutStarted } from '@repo/ui/src/services/analytics';
import useBasket from '@hooks/basket/useBasket';
import { GraphQLRequestWithTokenProps } from '@repo/ui/src/utils/graphql-request';
import {
  gcToAnalytics,
  basketLineInputToAnalytics,
} from '@repo/ui/src/services/analytics/factories';

const SESSION_BRIDGE_QUERY = gql`
  mutation CreateSessionBridge {
    createSessionBridge
  }
`;

export const verifyBasket = gql`
  query verifyBasket {
    verifyBasket {
      items
    }
  }
`;

interface Arg {
  skipVerification?: boolean;
  skipSession?: boolean;
}
const fetcher = async (
  props: GraphQLRequestWithTokenProps,
  {
    arg: { skipVerification = false, skipSession = false } = {},
  }: {
    arg: Arg;
  },
) => {
  if (!skipVerification) {
    const outOfStock = await basketRequest({ ...props, document: verifyBasket });
    if (outOfStock.verifyBasket.items?.length) {
      throw new OutOfStockError(outOfStock.verifyBasket.items);
    }
  }
  if (!skipSession) {
    const maxRetries = 3;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        // Increase timeout for each retry
        const timeoutMs = 15000 + (attempt - 1) * 5000; // 15s, 20s, 25s
        const timeoutPromise = new Promise((_, reject) => {
          setTimeout(
            () => reject(new Error(`Session bridge creation timeout (${timeoutMs}ms)`)),
            timeoutMs,
          );
        });

        const result = await Promise.race([basketRequest(props), timeoutPromise]);

        return result;
      } catch {
        if (attempt < maxRetries) {
          // Wait 1 second before retry
          await new Promise((resolve) => setTimeout(resolve, 1000));
        }
      }
    }

    // Return null to indicate session bridge failed but allow checkout to proceed
    return null;
  }
};

const clearSFCCCookies = () =>
  document.cookie
    .split(';')
    .filter(
      (cookie) =>
        cookie.startsWith('dwanonymous') ||
        cookie.startsWith('dwsid') ||
        cookie.startsWith('dwsecuretoken'),
    )
    .forEach((cookie) => {
      const [cookieName] = cookie.split('=');
      Cookies.remove(cookieName);
    });

const useGoToCheckout = () => {
  const { currency, locale } = useCurrentUrl();
  const { data: basket } = useBasket();
  const idToken = useCustomerIdToken();

  const { trigger, ...swrValues } = useSWRMutation(
    { document: SESSION_BRIDGE_QUERY, locale, idToken },
    fetcher,
  );

  const goToCheckout = useCallback(
    async ({ skipVerification, skipSession }: Arg = {}) => {
      const result = await trigger({
        skipVerification: skipVerification || !basket?.lineItems?.length,
        skipSession,
      });
      if (!skipSession) {
        // clear previous id token
        clearSFCCCookies();
        result?.createSessionBridge?.forEach((cookie: string) => {
          document.cookie = cookie.replace('HttpOnly; ', '');
        });
      }
      track(checkoutStarted, {
        affiliation: 'website',
        currency,
        locale,
        brand: 'Traeger',
        basket_token: basket?.basketJWT,
        products: [
          ...(basket?.lineItems?.map(basketLineInputToAnalytics) ?? []),
          ...(basket?.giftCards?.map(gcToAnalytics) ?? []),
        ],
        checkout_id: basket?.id ?? '',
      });
      return result;
    },
    [trigger, basket, locale, currency],
  );
  return { trigger: goToCheckout, ...swrValues };
};

export default useGoToCheckout;
